import { useEffect, useRef, useState } from 'react';
import { submitTeamInterest, type TeamInterestSize } from '../team-interest';
import { usePlanBuilderConfigContext } from '../PlanBuilderConfigContext';

interface Props {
  open: boolean;
  onClose(): void;
}

export function TeamModal({ open, onClose }: Props) {
  const config = usePlanBuilderConfigContext();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [org, setOrg] = useState('');
  const [size, setSize] = useState<TeamInterestSize | ''>('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const firstInputRef = useRef<HTMLInputElement>(null);

  // Lock body scroll, focus first input, attach Escape handler, restore on close.
  useEffect(() => {
    if (!open) return;
    const trigger = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    firstInputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      trigger?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const submit = async () => {
    setError(null);
    if (!name.trim() || !email.trim()) {
      setError('Name and work email are required.');
      return;
    }
    setBusy(true);
    try {
      await submitTeamInterest({
        name: name.trim(),
        email: email.trim(),
        organisation: org.trim() || undefined,
        team_size: size || undefined,
      });
      setSubmitted(true);
    } catch {
      setError("Couldn't submit — check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="pb-modal-overlay open"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pb-tm-title"
      onClick={onClose}
    >
      <div className="pb-modal-box" onClick={(e) => e.stopPropagation()}>
        <button className="pb-modal-close" onClick={onClose} aria-label="Close">
          <svg
            width="14"
            height="14"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.4}
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="pb-modal-badge">Team Mode · Early Access</div>
        <h2 className="pb-modal-title" id="pb-tm-title">
          {config.teamModal.title}
        </h2>

        {!submitted && (
          <>
            <p className="pb-modal-sub">
              {config.teamModal.body}
            </p>

            <div className="pb-cap-form">
              <div className="pb-cap-field">
                <label htmlFor="pb-tm-name">
                  Your name <span className="pb-required">*</span>
                </label>
                <input
                  id="pb-tm-name"
                  ref={firstInputRef}
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Smith"
                />
              </div>

              <div className="pb-cap-field">
                <label htmlFor="pb-tm-email">
                  Work email <span className="pb-required">*</span>
                </label>
                <input
                  id="pb-tm-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@company.com"
                />
              </div>

              <div className="pb-cap-field">
                <label htmlFor="pb-tm-org">Organisation</label>
                <input
                  id="pb-tm-org"
                  type="text"
                  value={org}
                  onChange={(e) => setOrg(e.target.value)}
                  placeholder="Acme Legal"
                />
              </div>

              <div className="pb-cap-field">
                <label htmlFor="pb-tm-size">How many people on your legal team?</label>
                <select
                  id="pb-tm-size"
                  value={size}
                  onChange={(e) => setSize(e.target.value as TeamInterestSize | '')}
                >
                  <option value="">Select...</option>
                  <option value="1-5">1&#x2013;5</option>
                  <option value="6-15">6&#x2013;15</option>
                  <option value="16-50">16&#x2013;50</option>
                  <option value="51-200">51&#x2013;200</option>
                  <option value="200+">200+</option>
                </select>
              </div>

              {error && (
                <div className="pb-cap-error" role="alert">
                  {error}
                </div>
              )}

              <div className="pb-cap-actions">
                <button className="pb-cap-submit" onClick={submit} disabled={busy}>
                  {busy ? 'Submitting…' : config.teamModal.ctaLabel}
                </button>
                <button className="pb-cap-cancel" onClick={onClose}>
                  Cancel
                </button>
              </div>
            </div>
          </>
        )}

        {submitted && (
          <>
            <p className="pb-modal-sub">
              Thanks &#x2014; we've got your details. We'll be in touch when team mode is
              ready for your group.
            </p>
            <div className="pb-cap-actions">
              <button className="pb-cap-submit" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
